<template>
  <div>
    <!-- Mobile Backdrop -->
    <div
      v-if="isOpen"
      @click="$emit('toggle')"
      class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
    ></div>
    
    <!-- Sidebar -->
    <aside
      :class="[
        'fixed top-0 left-0 z-50 h-screen bg-white border-r border-gray-200 transition-all duration-300',
        isOpen ? 'w-64' : 'w-20',
        'lg:block'
      ]"
    >
      <!-- Logo -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200">
        <div class="flex items-center">
          <div class="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
            <HeartIcon class="w-6 h-6 text-white" />
          </div>
          <span v-if="isOpen" class="ml-3 text-xl font-bold text-gray-900">
            ElderCare
          </span>
        </div>
        <button
          @click="$emit('toggle')"
          class="lg:hidden p-1 rounded-lg hover:bg-gray-100"
        >
          <XMarkIcon class="w-5 h-5" />
        </button>
      </div>
      
      <!-- Navigation -->
      <nav class="p-4 space-y-2">
        <RouterLink
          v-for="item in menuItems"
          :key="item.path"
          :to="item.path"
          v-slot="{ isActive }"
          custom
        >
          <a
            :href="item.path"
            @click.prevent="navigate(item.path)"
            :class="[
              'flex items-center px-3 py-2 rounded-lg transition-colors',
              isActive
                ? 'bg-primary-50 text-primary-600'
                : 'text-gray-700 hover:bg-gray-100'
            ]"
            :title="!isOpen ? item.label : ''"
          >
            <component :is="item.icon" class="w-5 h-5 flex-shrink-0" />
            <span v-if="isOpen" class="ml-3">{{ item.label }}</span>
            <span
              v-if="item.badge"
              class="ml-auto bg-danger-500 text-white text-xs px-2 py-0.5 rounded-full"
            >
              {{ item.badge }}
            </span>
          </a>
        </RouterLink>
      </nav>
      
      <!-- User Profile (Bottom) -->
      <div class="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
        <div class="flex items-center">
          <div class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
            <UserIcon class="w-6 h-6 text-gray-600" />
          </div>
          <div v-if="isOpen" class="ml-3 flex-1">
            <p class="text-sm font-medium text-gray-900">{{ user?.first_name }} {{ user?.last_name }}</p>
            <p class="text-xs text-gray-500">{{ user?.email }}</p>
          </div>
          <button
            v-if="isOpen"
            @click="handleLogout"
            class="ml-2 p-1 text-gray-500 hover:text-gray-700"
            title="Logout"
          >
            <ArrowRightOnRectangleIcon class="w-5 h-5" />
          </button>
        </div>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import {
  HomeIcon,
  UserGroupIcon,
  PhoneIcon,
  CalendarIcon,
  ChartBarIcon,
  BellAlertIcon,
  Cog6ToothIcon,
  HeartIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['toggle'])

const router = useRouter()
const authStore = useAuthStore()
const user = computed(() => authStore.currentUser)

const menuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: HomeIcon },
  { path: '/profiles', label: 'Elder Profiles', icon: UserGroupIcon },
  { path: '/calls', label: 'Calls', icon: PhoneIcon },
  { path: '/schedule', label: 'Schedule', icon: CalendarIcon },
  { path: '/analytics', label: 'Analytics', icon: ChartBarIcon },
  { path: '/alerts', label: 'Alerts', icon: BellAlertIcon, badge: 3 },
  { path: '/settings', label: 'Settings', icon: Cog6ToothIcon }
]

function navigate(path) {
  router.push(path)
}

async function handleLogout() {
  await authStore.logout()
}
</script>